import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { useSSEStream } from '../../../hooks/useSSEStream';
import { toNotificationRoute } from '../lib/notificationRoute';
import { toToastMessage } from '../lib/notificationToast';

interface UseNotificationStreamOptions {
  organizationId: string | undefined;
  enabled?: boolean;
  getAccessToken?: () => Promise<string | null>;
}

export function useNotificationStream({
  organizationId,
  enabled = true,
  getAccessToken,
}: UseNotificationStreamOptions) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const router = useRouter();

  const handleMessage = useCallback(
    (data: string) => {
      if (!data) return;

      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }

      if (
        !parsed ||
        typeof parsed !== 'object' ||
        typeof (parsed as { notificationId?: unknown }).notificationId !== 'string'
      ) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unread-count'],
      });
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'list'],
      });

      // A WhatsApp reply used to change nothing anywhere unless the
      // conversation screen happened to be open. The bell count moving is not
      // something anyone watches; a line that says who wrote and what they said
      // is.
      const message = toToastMessage(parsed as { title?: string; body?: string; merged?: boolean });
      if (!message) return;

      // Tappable only when there is somewhere to go. Announcing a reply without
      // a way to open it leaves the reader with a fact and a navigation
      // problem — open the menu, find WhatsApp, find the conversation — by
      // which time announcing it instantly has bought nothing.
      const route = toNotificationRoute(parsed as { subType?: string; data?: unknown });

      showToast({
        message,
        type: 'info',
        duration: 5000,
        onPress: route ? () => router.push(route as never) : undefined,
      });
    },
    [queryClient, showToast, router],
  );

  const { isConnected } = useSSEStream({
    path: 'notifications/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { isConnected };
}
